import { Link } from "react-router-dom";
import { Briefcase, Users, LineChart, ArrowRight } from "lucide-react";

// Three-card section explaining the three kinds of people who use Upbrief.
// Helps brand-new visitors instantly self-identify before they click anything.
const Roles = () => {
  return (
    <section id="roles" className="py-16 bg-background">
      <div className="container px-6 mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-3">
            Who Upbrief is for
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-3">
            Three roles. One simple flow.
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Managers run it. Contributors fill out a form. Executives read the finished report.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          <RoleCard
            icon={<Briefcase className="w-5 h-5" />}
            tag="You start here"
            title="Manager"
            description="Owns the account. Builds the template, invites contributors and executives, edits and publishes the report."
            bullet="Signs up at upbrief.app"
          />
          <RoleCard
            icon={<Users className="w-5 h-5" />}
            tag="No account needed"
            title="Contributor"
            description="Gets a personal link by email each cycle. Fills out a short form on phone or laptop. Done in five minutes."
            bullet="Optional dashboard to track their own achievements"
          />
          <RoleCard
            icon={<LineChart className="w-5 h-5" />}
            tag="Invited by manager"
            title="Executive viewer"
            description="Read-only access to finalized reports and analytics. No editing, no chasing — just the briefing they need."
            bullet="Signs in with the email the manager invited"
          />
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            See the full step-by-step <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const RoleCard = ({
  icon,
  tag,
  title,
  description,
  bullet,
}: {
  icon: React.ReactNode;
  tag: string;
  title: string;
  description: string;
  bullet: string;
}) => {
  return (
    <div className="p-6 rounded-lg bg-card border border-border/60 flex flex-col h-full">
      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
        {icon}
      </div>
      <p className="text-[11px] font-medium tracking-[0.15em] uppercase text-muted-foreground mb-1.5">
        {tag}
      </p>
      <h3 className="font-semibold text-foreground text-lg mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground mb-4 flex-1">{description}</p>
      <p className="text-xs text-muted-foreground border-t border-border/60 pt-3">
        {bullet}
      </p>
    </div>
  );
};

export default Roles;